// Admin: Women's Circle Management
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Calendar, Clock, Users, Loader2,
  Pencil, Trash2, XCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { AuthContext } from '../../App';
import { GlobalNav } from '../../components/GlobalNav';

const TOPIC_TAGS = [
  { key: 'boundaries', label: 'Boundaries & Consent' },
  { key: 'sisterwives', label: 'Sister Wives & Family' },
  { key: 'healing', label: 'Healing & Pacing' },
  { key: 'motherhood', label: 'Motherhood' },
  { key: 'culture', label: 'Culture & Community' },
  { key: 'newhere', label: 'New Here' },
];

function formatDT(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function toLocalInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminCirclesPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '', description: '', host_label: 'Unity Love Team',
    start_at: '', end_at: '', topic_tag: 'newhere', capacity: '',
  });

  const fetchEvents = async () => {
    try {
      const res = await apiClient.get('/her-space/admin/circles', { headers });
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load circles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', host_label: 'Unity Love Team', start_at: '', end_at: '', topic_tag: 'newhere', capacity: '' });
    setEditingEvent(null);
    setShowForm(false);
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      host_label: ev.host_label || 'Unity Love Team',
      start_at: toLocalInput(ev.start_at),
      end_at: toLocalInput(ev.end_at),
      topic_tag: ev.topic_tag || 'newhere',
      capacity: ev.capacity || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_at || !form.end_at) {
      toast.error('Title, start and end times are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        host_label: form.host_label,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        topic_tag: form.topic_tag,
        capacity: form.capacity ? parseInt(form.capacity) : null,
      };

      if (editingEvent) {
        await apiClient.patch(`/her-space/admin/circles/${editingEvent.id}`, payload, { headers });
        toast.success('Circle updated');
      } else {
        await apiClient.post('/her-space/admin/circles', payload, { headers });
        toast.success('Circle created');
      }
      resetForm();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete/cancel "${ev.title}"?`)) return;
    try {
      await apiClient.delete(`/her-space/admin/circles/${ev.id}`, { headers });
      toast.success('Circle removed');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleCancel = async (ev) => {
    if (!window.confirm(`Cancel "${ev.title}"?`)) return;
    try {
      await apiClient.patch(`/her-space/admin/circles/${ev.id}`, { status: 'canceled' }, { headers });
      toast.success('Circle canceled');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  const statusColor = {
    scheduled: 'text-blue-600 dark:text-blue-400',
    live: 'text-green-600 dark:text-green-400',
    ended: 'text-gray-500',
    canceled: 'text-red-500',
  };

  return (
    <div className="min-h-screen surface-page">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
        <button onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition mb-5"
          data-testid="admin-circles-back-btn">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Admin Home</span>
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Women's Circles</h1>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition"
            data-testid="create-circle-btn">
            <Plus className="w-4 h-4" />
            New Circle
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="surface-card rounded-2xl p-5 shadow-sm mb-6" data-testid="circle-form">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingEvent ? 'Edit Circle' : 'Create Circle'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input type="text" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                  required data-testid="circle-form-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                  data-testid="circle-form-desc" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start</label>
                  <input type="datetime-local" value={form.start_at} onChange={(e) => setForm(f => ({ ...f, start_at: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                    required data-testid="circle-form-start" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End</label>
                  <input type="datetime-local" value={form.end_at} onChange={(e) => setForm(f => ({ ...f, end_at: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                    required data-testid="circle-form-end" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Topic</label>
                  <select value={form.topic_tag} onChange={(e) => setForm(f => ({ ...f, topic_tag: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                    data-testid="circle-form-topic">
                    {TOPIC_TAGS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Host</label>
                  <input type="text" value={form.host_label} onChange={(e) => setForm(f => ({ ...f, host_label: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-300"
                    data-testid="circle-form-host" />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition disabled:opacity-50"
                  data-testid="circle-form-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {editingEvent ? 'Save Changes' : 'Create Circle'}
                </button>
                <button type="button" onClick={resetForm}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
                  data-testid="circle-form-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Events List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No circle events yet. Create the first one!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-circles-list">
            {events.map((ev) => (
              <div key={ev.id} className="surface-card rounded-2xl p-4 shadow-sm" data-testid={`admin-circle-${ev.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold uppercase ${statusColor[ev.computed_status] || ''}`}>
                        {ev.computed_status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {TOPIC_TAGS.find(t => t.key === ev.topic_tag)?.label || ev.topic_tag}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-1">{ev.title}</h3>
                    <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDT(ev.start_at)}</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.rsvp_count} going</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-3 flex-shrink-0">
                    {ev.computed_status === 'scheduled' && (
                      <>
                        <button onClick={() => openEdit(ev)} title="Edit"
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition text-gray-500"
                          data-testid={`edit-circle-${ev.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleCancel(ev)} title="Cancel"
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition text-red-500"
                          data-testid={`cancel-circle-${ev.id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {(ev.computed_status === 'ended' || ev.computed_status === 'canceled') && (
                      <button onClick={() => handleDelete(ev)} title="Delete"
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition text-red-500"
                        data-testid={`delete-circle-${ev.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
